import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, exhaustMap, filter, fromEvent, interval, Subject, Subscription, takeUntil, timeout, TimeoutError, timer } from 'rxjs';
import { CodeSigninService } from './../_services/code-signin.service';
import { Component, OnInit, ViewEncapsulation, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { SignInCode } from '../_interface/signiCode';
import { NGXLogger } from 'ngx-logger';
import { environment } from 'src/environments/environment';
import { checkInternet, InternetState, isLocalHostname } from '../_helpers/internet-check';
import { ConnectionStatusService } from '../_services/connection-status.service';

/** Zahtev ne sme da visi bez kraja (SW "freshness" bez timeout-a, TCP bez odgovora) — inače nema
 *  ni greške ni novog pokušaja. */
const REQUEST_TIMEOUT_MS = 10000;
/** Novi pokušaj dobijanja koda posle neuspeha (i odmah kad se mreža vrati) — bez kraja. */
const RETRY_MS = 5000;
/** Javne servere (provera interneta) zovemo najviše jednom u ovom periodu dok server ne odgovara. */
const INTERNET_RECHECK_MS = 30000;

type FailureKind = 'offline' | 'timeout' | 'unreachable' | 'server' | 'rejected' | 'unknown';

@Component({
    selector: 'app-code-signin',
    templateUrl: './code-signin.component.html',
    styleUrls: ['./code-signin.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CodeSigninComponent implements OnInit, OnDestroy {
  destroy = new Subject<void>();

  progressbarTimer?: Subscription;
  currentSec = 0;

  /**
   * Trenutak generisanja i isteka koda po zidnom satu. Neaktivan tab guši tajmere
   * (setInterval ide na ~1/min), pa brojanje tikova zaostaje — kad se tab vrati u fokus
   * prikazano preostalo vreme bi bilo veće nego što stvarno jeste, a kod bi već bio mrtav.
   */
  private startedAtMs = 0;
  private expiresAtMs = 0;

  justCopied = false;
  private copyResetTimer?: any;

  /** Zašto kod nije dobijen (poslednji neuspeh) — prikazuje se dok se ponovo pokušava. */
  failure?: { kind: FailureKind; status?: number };
  failedAttempts = 0;
  /** Uzastopni neuspesi prijave kodom (poll) — veza sa serverom je pukla dok je kod na ekranu. */
  loginFailStreak = 0;

  private generateSub?: Subscription;
  private retrySub?: Subscription;
  private internet?: InternetState;
  private internetCheckedAt = 0;
  private internetChecking = false;

  @Input()
  successLoginRedirect = '';

  /** Kod je otvoren klikom ("eMeni ručna prijava", konobar). Dok status veze iznad prikazuje
   *  prekid, tada ostaje "Preuzimam kod…" (klik mora nešto da pokaže); kod prikazan sam od
   *  sebe (KD) se tada ne prikazuje — status već kaže sve. */
  @Input()
  openedByUser = false;

  @Output() loginCompleted = new EventEmitter();

  private _code?: SignInCode | undefined;
  public get code(): SignInCode | undefined {
    return this._code;
  }
  public set code(value: SignInCode | undefined) {
    this._code = value;

    if (value) {
      this.startedAtMs = Date.now();
      this.expiresAtMs = this.startedAtMs + Math.max(0, value.expires) * 1000;
      this.startProgressbarTimer(value.expires);
      //
    }
  }

  constructor(private codeSigninService: CodeSigninService,
    private logger: NGXLogger,
    private router: Router,
    private connectionStatus: ConnectionStatusService) {
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();

    //this.stopLoginTimer();
    this.progressbarTimer?.unsubscribe();

  }

  ngOnInit(): void {
    this.generateCode();
    this.startLoginTimer();

    // Mreža se vratila — ne čekaj sledeći krug, pokušaj odmah.
    fromEvent(window, 'online').pipe(
      takeUntil(this.destroy)
    ).subscribe(() => {
      if (!this.code) {
        // stari razlog ("nije na mreži") više ne važi — do odgovora piše "Preuzimam kod…"
        this.failure = undefined;
        this.generateCode();
      }
    });
  }

  generateCode() {
    this.retrySub?.unsubscribe();
    // zahtev koji još visi se otkazuje — da kasniji neuspeh ne obriše kod koji je u međuvremenu stigao
    this.generateSub?.unsubscribe();
    this.generateSub = this.codeSigninService.generate().pipe(
      timeout(REQUEST_TIMEOUT_MS),
      takeUntil(this.destroy)
    ).subscribe({
      next: code => {
        if (!code) {
          this.onGenerateFailed(undefined);
          return;
        }
        this.failure = undefined;
        this.failedAttempts = 0;
        this.code = code;
      },
      error: e => this.onGenerateFailed(e)
    });
  }

  /** Neuspeh → razlog na ekran, pa novi pokušaj za RETRY_MS — dok server ne odgovori. */
  private onGenerateFailed(e: unknown) {
    const kind = this.failureKind(e);
    if (this.failedAttempts === 0 || kind !== this.failure?.kind) {
      // u log samo početak niza i promena razloga — pokušaji idu na 5 s
      this.logger.warn('Signin code: generate failed', kind, e);
    }
    this.code = undefined;
    this.failedAttempts++;
    this.failure = { kind, status: e instanceof HttpErrorResponse ? e.status : undefined };

    if (kind === 'unreachable' || kind === 'timeout') {
      this.refreshInternetState();
    }

    this.retrySub = timer(RETRY_MS).pipe(
      takeUntil(this.destroy)
    ).subscribe(() => this.generateCode());
  }

  private failureKind(e: unknown): FailureKind {
    if (navigator.onLine === false) {
      return 'offline';
    }
    if (e instanceof TimeoutError) {
      return 'timeout';
    }
    if (e instanceof HttpErrorResponse) {
      // 0 = mrežna greška; 504 = isto, ali ga vrati service worker (dataGroup bez mreže)
      if (e.status === 0 || e.status === 504) {
        return 'unreachable';
      }
      return e.status >= 500 ? 'server' : 'rejected';
    }
    return 'unknown';
  }

  /** Server ne odgovara — proveri internet (najviše na INTERNET_RECHECK_MS) da poruka kaže zašto. */
  private refreshInternetState() {
    if (this.internetChecking || this.backendIsLocal ||
      (this.internet && Date.now() - this.internetCheckedAt < INTERNET_RECHECK_MS)) {
      return;
    }
    this.internetChecking = true;
    checkInternet().then(state => {
      this.internetChecking = false;
      this.internet = state;
      this.internetCheckedAt = Date.now();
    });
  }

  /** Prekid veze već prikazuje status veze iznad (login ekran) — ovde bez druge poruke o tome. */
  get connectionIssueShown(): boolean {
    return this.connectionStatus.showing$.value;
  }

  /** Razlog za prikaz (i18n ključ + parametri). Kad server ne odgovara, provera interneta kaže zašto. */
  get failureMessage(): { key: string; params?: object } | undefined {
    const f = this.failure;
    if (!f) {
      return undefined;
    }
    switch (f.kind) {
      case 'offline': return { key: 'codeSignin.errors.offline' };
      case 'server': return { key: 'codeSignin.errors.server', params: { status: f.status } };
      case 'rejected': return { key: 'codeSignin.errors.rejected', params: { status: f.status } };
      case 'unknown': return { key: 'codeSignin.errors.unknown' };
    }
    const host = this.backendHost;
    if (this.backendIsLocal) {
      return { key: 'codeSignin.errors.localServer', params: { host } };
    }
    switch (this.internet) {
      case 'down': return { key: 'codeSignin.errors.noInternet' };
      case 'dns': return { key: 'codeSignin.errors.dns' };
      case 'ok': return { key: 'codeSignin.errors.serverDown', params: { host } };
    }
    return { key: f.kind === 'timeout' ? 'codeSignin.errors.timeout' : 'codeSignin.errors.unreachable', params: { host } };
  }

  private get backendHost(): string {
    try {
      return new URL(environment.apiBase, location.href).host;
    } catch {
      return location.host;
    }
  }

  private get backendIsLocal(): boolean {
    try {
      return isLocalHostname(new URL(environment.apiBase, location.href).hostname);
    } catch {
      return false;
    }
  }

  startLoginTimer() {
    interval(5000).pipe(
      filter(() => !!this.code),
      // jedan zahtev u letu — bez gomilanja kad server ne odgovara
      exhaustMap(() => this.codeSigninService.tryLogin(this.code!.code, this.code!.token).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        catchError(e => {
          if (this.loginFailStreak === 0) {
            this.logger.warn("Error signing in with code", e);
          }
          this.loginFailStreak++;
          return EMPTY;
        })
      )),
      takeUntil(this.destroy)
    ).subscribe(jwtToken => {
      this.loginFailStreak = 0;
      if (jwtToken) {
        this.loginCompleted.emit(jwtToken);
      }
    });
  }


  startProgressbarTimer(seconds: number) {
    const timer$ = interval(100);

    this.progressbarTimer?.unsubscribe();

    this.progressbarTimer =
      timer$.pipe(
        takeUntil(this.destroy)
      ).subscribe(() => {
        // Proteklo vreme se cita sa sata, ne iz broja tikova - tik moze da izostane.
        this.currentSec = Math.min(seconds, (Date.now() - this.startedAtMs) / 1000);

        if (this.remainingSeconds <= 0) {
          this.progressbarTimer?.unsubscribe();
          this.generateCode();

        }
      });
  }

  /** Preostale sekunde do isteka koda, po zidnom satu. */
  private get remainingSeconds(): number {
    if (!this._code) { return 0; }
    return Math.max(0, Math.ceil((this.expiresAtMs - Date.now()) / 1000));
  }

  /** Seconds remaining until the code refreshes, formatted "M:SS". */
  get remainingTime(): string {
    if (!this.code) { return ''; }
    const remaining = this.remainingSeconds;
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  /** Copy the device code to the clipboard, with a brief "Kopirano!" feedback. */
  copyCode() {
    if (!this.code) { return; }
    const text = this.code.code;
    const done = () => this.showCopied();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done(); // older browsers / insecure context — still show feedback
    }
  }

  private showCopied() {
    this.justCopied = true;
    clearTimeout(this.copyResetTimer);
    this.copyResetTimer = setTimeout(() => { this.justCopied = false; }, 1600);
  }

}
